import React, { useState, useRef, useMemo, useEffect } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Paperclip, Megaphone, X, Clock, UserMinus, Search, FlaskConical, Gauge, Users, Layers, Eye, Pencil, Copy, AlertTriangle, Wand2 } from 'lucide-react';
import { PaceEditor, pacePerDay } from './CampaignPaceEditor';
import { CcPicker } from './CcPicker';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.innerText || '').trim();
};

const LINK_DUP_SRC = '\\[\\s*((?:https?:\\/\\/|www\\.)[^\\]\\s]+?)\\s*\\]\\s*((?:https?:\\/\\/|www\\.)[^\\s<]+)';
const normUrl = (u) => (u || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();

const dedupLinks = (t) => (t || '').replace(new RegExp(LINK_DUP_SRC, 'g'), (m, a, b) => {
  const na = normUrl(a); const nb = normUrl(b);
  return na === nb || na.startsWith(nb) || nb.startsWith(na) ? b : m;
});

const HARD_WRAP_MIN = 60;
const endsSentence = (t) => /[.!?:;]$/.test((t || '').trim());

const templateBlocks = (htmlStr) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = htmlStr || '';
  return Array.from(tmp.children);
};

// Paste artifacts that wreck the sent email: "[url]url" duplicates or hard-wrapped lines
const needsCleanup = (htmlStr) => {
  if (new RegExp(LINK_DUP_SRC).test(stripHtml(htmlStr))) return true;
  const ps = templateBlocks(htmlStr).filter(el => el.tagName === 'P');
  for (let i = 0; i < ps.length - 1; i++) {
    const a = (ps[i].textContent || '').trim();
    const b = (ps[i + 1].textContent || '').trim();
    if (a && b && a.length >= HARD_WRAP_MIN && !endsSentence(a)) return true;
  }
  return false;
};

const cleanTemplate = (htmlStr) => {
  const out = [];
  let prevBlank = true;
  templateBlocks(dedupLinks(normalizeQuillHtml(htmlStr || ''))).forEach(el => {
    if (el.tagName !== 'P') { out.push(el.outerHTML); prevBlank = false; return; }
    const text = (el.textContent || '').trim();
    if (!text) {
      if (!prevBlank) out.push('<p><br></p>');
      prevBlank = true;
      return;
    }
    const last = out.length ? out[out.length - 1] : '';
    const lastText = last.startsWith('<p') ? stripHtml(last) : '';
    if (!prevBlank && lastText && lastText.length >= HARD_WRAP_MIN && !endsSentence(lastText)) {
      const joiner = /-$/.test(lastText) ? '' : ' ';
      out[out.length - 1] = last.replace(/<\/p>$/, joiner + el.innerHTML.trim() + '</p>');
    } else {
      out.push('<p>' + el.innerHTML + '</p>');
    }
    prevBlank = false;
  });
  while (out.length && !stripHtml(out[out.length - 1])) out.pop();
  return out.join('');
};

// Official OLTA signature is appended by the backend — detect & strip pasted copies
const SIG_LINE_RES = [
  /^talu hakverdi\b/i,
  /^olta nachfolge gmbh$/i,
  /annastra(ß|ss)e\s*58/i,
  /^52062\s+aachen(,?\s*germany)?$/i,
  /5570\s*3830/,
  /^(\[[^\]]*\]\s*)?(https?:\/\/)?www\.olta-nachfolge\.com\/?$/i,
  /^[-–_]{2,}$/,
  /this message and its contents are confidential/i,
  /any disclosure, use, copying or distribution/i,
];

const hasPastedSignature = (htmlStr) => {
  const t = stripHtml(htmlStr).toLowerCase();
  return /annastra(ß|ss)e/.test(t) || /5570\s*3830/.test(t);
};

const stripSignature = (htmlStr) => {
  const out = [];
  let prevBlank = true;
  templateBlocks(normalizeQuillHtml(htmlStr || '')).forEach(el => {
    const text = (el.textContent || '').trim();
    if (text && SIG_LINE_RES.some(re => re.test(text))) return;
    const blank = !text;
    if (blank && prevBlank) return;
    out.push(blank ? '<p><br></p>' : el.outerHTML);
    prevBlank = blank;
  });
  while (out.length && !stripHtml(out[out.length - 1])) out.pop();
  return out.join('');
};

export const SignaturePreview = ({ name, phone = '+49 241 5570 3830' }) => (
  <div data-testid="campaign-signature-preview">
    <p style={{ margin: '14px 0 0', fontWeight: 700 }}>{name}</p>
    <p style={{ margin: '10px 0 0' }}>OLTA Nachfolge GmbH<br />Annastraße 58-60, 52062 Aachen, Germany</p>
    <p style={{ margin: '10px 0 0' }}>{phone}</p>
    <p style={{ margin: '10px 0 0' }}><a href="https://www.olta-nachfolge.com" className="text-blue-600 underline">www.olta-nachfolge.com</a></p>
    <p style={{ margin: '18px 0 0' }} className="text-[11px] text-zinc-400">----<br />
      This message and its contents are confidential, may be legally privileged, and are intended only for the use
      of the named recipient. If you are not the intended recipient, please destroy it and all its attachments,
      if any, from your system and notify the sender immediately. Any disclosure, use, copying or distribution,
      either in whole or in part, of this message is prohibited. Thank you for your cooperation.</p>
  </div>
);

const SAMPLE_CTX = { first_name: 'Erika', last_name: 'Mustermann', title: '',
  full_name: 'Erika Mustermann', company: 'Muster M&A GmbH', email: 'erika@muster-ma.de' };

const renderTpl = (tpl, ctx) =>
  (tpl || '').replace(/\{(first_name|last_name|title|full_name|company)\}/g, (_, k) => ctx[k] || '');

const asHtml = (t) => ((t || '').trim().startsWith('<') ? t : '<p>' + (t || '').replace(/\n/g, '<br>') + '</p>');

// Quill 2 getSemanticHTML() turns every space into &nbsp; (breaks word-wrap in Outlook)
// and strips <br> from blank lines — normalize before preview/submit
const normalizeQuillHtml = (h) => (h || '')
  .replace(/&nbsp;/g, ' ')
  .replace(/\u00a0/g, ' ')
  .replace(/<p><\/p>/g, '<p><br></p>');

const DEFAULT_STEPS = [
  {
    subject: 'Vorstellung OLTA Nachfolge — unser Suchprofil',
    delay_days: 0,
    template: '<p>Sehr geehrte(r) {title} {last_name},</p><p><br></p>'
      + '<p>wir sind <strong>OLTA Nachfolge</strong> und übernehmen mittelständische Unternehmen im Rahmen von Nachfolgelösungen — eigenkapitalstark, unternehmerisch und mit langfristigem Horizont.</p><p><br></p>'
      + '<p>Gerne möchten wir uns bei Ihnen als Käufer für passende Mandate vorstellen. Anbei finden Sie unser Suchprofil mit allen Details zu Branchen, Größenordnung und Transaktionsstruktur.</p><p><br></p>'
      + '<p>Über die Aufnahme in Ihren Verteiler und die Zusendung passender Teaser freuen wir uns sehr.</p><p><br></p>'
      + '<p>Beste Grüße</p>',
  },
  {
    subject: 'Re: Vorstellung OLTA Nachfolge — unser Suchprofil',
    delay_days: 5,
    template: '<p>Sehr geehrte(r) {title} {last_name},</p><p><br></p>'
      + '<p>vor einigen Tagen habe ich Ihnen unser Suchprofil zugesendet. Gerne wollte ich kurz nachfassen: Haben Sie aktuell Mandate, die zu unserem Profil passen könnten?</p><p><br></p>'
      + '<p>Über einen kurzen Austausch freue ich mich.</p><p><br></p>'
      + '<p>Beste Grüße</p>',
  },
  {
    subject: 'Re: Vorstellung OLTA Nachfolge — unser Suchprofil',
    delay_days: 7,
    template: '<p>Sehr geehrte(r) {title} {last_name},</p><p><br></p>'
      + '<p>ich möchte mich ein letztes Mal in Erinnerung bringen — falls Sie künftig Verkaufsmandate im Mittelstand betreuen, nehmen Sie uns gerne in Ihren Käuferverteiler auf.</p><p><br></p>'
      + '<p>Vielen Dank und beste Grüße</p>',
  },
];

const contactLabel = (c) => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company || c.email || '—';

const DEFAULT_PACE_STATE = { preset: 'fast', min_gap_min: 2, max_gap_min: 10, daily_limit: '' };

export function CampaignDialog({ open, onOpenChange, contacts, onStarted, allBrokers = false }) {
  const { user } = useAuth();
  const sigName = /talu/i.test(user?.name || '') ? 'Talu Hakverdi, CFA' : (user?.name || '');
  const [name, setName] = useState('');
  const [steps, setSteps] = useState(DEFAULT_STEPS.map(s => ({ ...s })));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [excluded, setExcluded] = useState({});
  const [excludeSearch, setExcludeSearch] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [pace, setPace] = useState({ ...DEFAULT_PACE_STATE });
  const [cc, setCc] = useState([]);
  const [firstTranche, setFirstTranche] = useState('');
  const [preview, setPreview] = useState({});
  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [copiedFrom, setCopiedFrom] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.listCampaigns()
      .then(r => setPastCampaigns((r.data.campaigns || [])
        .filter(c => (c.steps || []).length > 0)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))))
      .catch(() => {});
  }, [open]);

  const copyFrom = (cid) => {
    const c = pastCampaigns.find(x => x.id === cid);
    if (!c) return;
    setCopiedFrom(cid);
    setSteps((c.steps || []).map(s => ({
      subject: s.subject || '', delay_days: s.delay_days ?? 0, template: asHtml(s.template || ''),
    })));
    setPreview({});
    toast.success(`Texts copied from "${c.name}" — ${(c.steps || []).length} email(s) loaded, edit freely`);
  };

  const isTest = !!testEmail.trim();
  const included = contacts.filter(c => !excluded[c.id]);
  const withEmail = included.filter(c => (c.email || '').trim());
  const withoutEmail = included.filter(c => !(c.email || '').trim());
  const alreadyEnrolled = withEmail.filter(c => c.enrolled);
  const available = withEmail.filter(c => !c.enrolled);
  const trancheN = parseInt(firstTranche, 10) || 0;
  const effectiveCount = trancheN > 0 ? Math.min(trancheN, available.length) : available.length;
  const excludedList = Object.values(excluded);

  const pvContact = !isTest && available[0];
  const pvCtx = pvContact ? {
    first_name: pvContact.first_name || '', last_name: pvContact.last_name || '',
    title: pvContact.title || '',
    full_name: [pvContact.first_name, pvContact.last_name].filter(Boolean).join(' ') || pvContact.company || '',
    company: pvContact.company || '', email: pvContact.email || '',
  } : SAMPLE_CTX;
  const pvName = pvCtx.full_name || pvCtx.company || pvCtx.email;

  const matches = useMemo(() => {
    const q = excludeSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return contacts
      .filter(c => !excluded[c.id] && `${c.first_name || ''} ${c.last_name || ''} ${c.company || ''} ${c.email || ''}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [excludeSearch, contacts, excluded]);

  const perDay = pacePerDay(pace);
  const estDays = Math.max(1, Math.ceil(effectiveCount / perDay));

  const setStep = (i, field, value) => setSteps(prev => prev.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
  const removeStep = (i) => setSteps(prev => prev.filter((_, j) => j !== i));
  const addStep = () => setSteps(prev => [...prev, { subject: 'Re: Vorstellung OLTA Nachfolge', delay_days: 7, template: '' }]);

  const excludeContact = (c) => { setExcluded(prev => ({ ...prev, [c.id]: c })); setExcludeSearch(''); };
  const unexclude = (id) => setExcluded(prev => { const n = { ...prev }; delete n[id]; return n; });

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('PDF too large — max 3 MB (Microsoft mail limit)'); return; }
    setFile(f);
  };

  const start = async () => {
    if (!name.trim()) { toast.error('Campaign name required'); return; }
    if (isTest && !/\S+@\S+\.\S+/.test(testEmail.trim())) { toast.error('Enter a valid test email address'); return; }
    if (!isTest && !available.length) { toast.error('Every selected broker with an email is already enrolled in a campaign'); return; }
    if (!steps.length || !stripHtml(steps[0].template)) { toast.error('The intro email needs a text'); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        steps: steps.map(s => ({ ...s, template: normalizeQuillHtml(s.template) })),
        cc_emails: cc,
        pace: { preset: pace.preset, min_gap_min: pace.min_gap_min, max_gap_min: pace.max_gap_min,
                daily_limit: pace.daily_limit === '' ? 0 : pace.daily_limit },
      };
      if (isTest) {
        body.test_email = testEmail.trim();
      } else if (allBrokers) {
        body.all_brokers = true;
        body.exclude_ids = Object.keys(excluded);
      } else {
        body.contact_ids = included.map(c => c.id);
      }
      if (!isTest && trancheN > 0) body.first_tranche = trancheN;
      const r = await api.createCampaign(body);
      const cid = r.data.campaign.id;
      if (file) {
        const b64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(String(reader.result).split(',')[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        await api.uploadCampaignAttachment(cid, { filename: file.name, content_b64: b64 });
      }
      const s = await api.startCampaign(cid);
      toast.success(isTest
        ? `Test campaign started — the whole sequence goes to ${testEmail.trim()} (delays in minutes)`
        : `Campaign started — ${s.data.recipients} broker(s) enrolled`);
      if (!isTest && r.data.skipped_in_campaign?.length) {
        const list = r.data.skipped_in_campaign;
        toast.info(`Skipped (already enrolled in a campaign — never contacted twice): ${list.slice(0, 10).join(', ')}${list.length > 10 ? ` +${list.length - 10} more` : ''}`);
      }
      if (!isTest && r.data.skipped_bounced?.length) {
        const list = r.data.skipped_bounced;
        toast.info(`Skipped (email bounced previously — fix the address on the Contacts page): ${list.slice(0, 10).join(', ')}${list.length > 10 ? ` +${list.length - 10} more` : ''}`);
      }
      if (!isTest && r.data.skipped_firm_replied?.length) {
        const list = r.data.skipped_firm_replied;
        toast.info(`Übersprungen (Firma hat bereits geantwortet — kein Kollege wird erneut kontaktiert): ${list.slice(0, 5).join(' · ')}${list.length > 5 ? ` +${list.length - 5} weitere` : ''}`, { duration: 12000 });
      }
      if (!isTest && r.data.firm_warnings?.length) {
        const list = r.data.firm_warnings;
        toast.warning(`Gleiche Firma mehrfach: ${list.slice(0, 3).join(' · ')}${list.length > 3 ? ` +${list.length - 3} weitere` : ''}`, { duration: 12000 });
      }
      setName(''); setSteps(DEFAULT_STEPS.map(x => ({ ...x }))); setFile(null);
      setExcluded({}); setExcludeSearch(''); setTestEmail(''); setPace({ ...DEFAULT_PACE_STATE }); setCc([]); setFirstTranche(''); setPreview({}); setCopiedFrom('');
      onOpenChange(false);
      onStarted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start campaign');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-foreground flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-sky-400" /> Start Broker Campaign
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-secondary/40 border border-border rounded-sm p-3 text-xs text-muted-foreground flex items-start gap-2">
            <Clock className="w-4 h-4 shrink-0 mt-0.5 text-sky-400" />
            <span>
              Emails are sent <span className="text-foreground">Mon–Fri 09:00–17:00 (Berlin)</span> at your chosen
              sending pace from your Microsoft mailbox. Follow-ups stop automatically when a broker replies.
              {!isTest && effectiveCount > 0 && (
                <span className="block mt-1 text-sky-300" data-testid="campaign-eta">
                  ≈ {estDays} business day{estDays === 1 ? '' : 's'} until every broker received the intro email (~{perDay} sends/day at the current pace).
                </span>
              )}
            </span>
          </div>

          <div className={`border rounded-sm p-3 space-y-1.5 ${isTest ? 'border-violet-500/50 bg-violet-600/10' : 'border-violet-600/30 bg-violet-600/5'}`}
            data-testid="campaign-test-box">
            <label className="text-xs text-foreground flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5 text-violet-400" /> Test run first (recommended) — send the whole sequence to one address only
            </label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="your@email.com — leave empty for the real campaign"
              data-testid="campaign-test-email"
              className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" />
            {isTest && (
              <p className="text-[10px] text-violet-300" data-testid="campaign-test-hint">
                Test mode: emails go out immediately (also outside business hours) and the follow-up delays count
                in <span className="font-bold">minutes instead of days</span> — reply to the test email to check the auto-stop.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Campaign name *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder={isTest ? 'e.g. Test Broker Intro' : 'e.g. Broker Intro Q3'}
              data-testid="campaign-name-input" className="bg-secondary border-border text-foreground rounded-sm h-9" />
          </div>

          <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2" data-testid="campaign-cc-box">
            <label className="text-xs text-foreground flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-emerald-400" /> CC colleagues — copied on every email of this campaign
            </label>
            <CcPicker value={cc} onChange={setCc} />
          </div>

          {!isTest && (
            <>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">
                  {allBrokers ? 'Every broker on your Contacts list:' : 'Recipients:'}{' '}
                  <span className="text-foreground font-medium" data-testid="campaign-recipient-count">{available.length}</span> available
                  {alreadyEnrolled.length > 0 && (
                    <span className="text-sky-300"> · {alreadyEnrolled.length} already in a campaign (auto-skipped)</span>
                  )}
                  {withoutEmail.length > 0 && (
                    <span className="text-amber-400"> · {withoutEmail.length} skipped (no email): {withoutEmail.slice(0, 10).map(contactLabel).join(', ')}{withoutEmail.length > 10 ? ` +${withoutEmail.length - 10} more` : ''}</span>
                  )}
                  {excludedList.length > 0 && <span className="text-red-400"> · {excludedList.length} excluded</span>}
                </p>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                  {available.slice(0, 30).map(c => (
                    <span key={c.id} className="text-[10px] font-mono border border-sky-600/40 bg-sky-600/10 text-sky-300 rounded px-1.5 py-0.5">
                      {contactLabel(c)}
                    </span>
                  ))}
                  {available.length > 30 && <span className="text-[10px] text-muted-foreground">+{available.length - 30} more</span>}
                </div>
              </div>

              <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-1.5" data-testid="campaign-tranche-box">
                <label className="text-xs text-foreground flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-sky-400" /> Start with a tranche (optional) — enroll only the first N brokers now, add the rest later
                </label>
                <Input type="number" min="1" value={firstTranche} onChange={e => setFirstTranche(e.target.value)}
                  placeholder={`leave empty to enroll all ${available.length}`} data-testid="campaign-tranche-input"
                  className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs w-56" />
                {trancheN > 0 && (
                  <p className="text-[10px] text-sky-300" data-testid="campaign-tranche-hint">
                    First tranche: {effectiveCount} broker(s) now — add the next tranche any time via "Add tranche" on the campaign card.
                  </p>
                )}
              </div>

              <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2" data-testid="campaign-exclude-box">
                <label className="text-xs text-foreground flex items-center gap-1.5">
                  <UserMinus className="w-3.5 h-3.5 text-red-400" /> Exclude brokers (e.g. already contacted manually)
                </label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={excludeSearch} onChange={e => setExcludeSearch(e.target.value)}
                    placeholder="Search name, company or email…" data-testid="campaign-exclude-search"
                    className="pl-8 bg-secondary border-border text-foreground rounded-sm h-8 text-xs" />
                </div>
                {matches.length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid="campaign-exclude-matches">
                    {matches.map(c => (
                      <button key={c.id} onClick={() => excludeContact(c)} data-testid={`campaign-exclude-match-${c.id}`}
                        className="text-[10px] font-mono border border-border bg-secondary/60 text-foreground/80 hover:border-red-500/50 hover:text-red-300 rounded px-1.5 py-0.5 transition-colors">
                        {contactLabel(c)}{c.company && ` · ${c.company}`}
                      </button>
                    ))}
                  </div>
                )}
                {excludeSearch.trim().length >= 2 && matches.length === 0 && (
                  <p className="text-[10px] text-muted-foreground italic">No matching broker found.</p>
                )}
                {excludedList.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1 border-t border-border/60" data-testid="campaign-excluded-list">
                    {excludedList.map(c => (
                      <span key={c.id} className="inline-flex items-center gap-1 text-[10px] font-mono border border-red-600/40 bg-red-600/10 text-red-300 rounded px-1.5 py-0.5">
                        {contactLabel(c)}
                        <button onClick={() => unexclude(c.id)} className="hover:text-foreground" data-testid={`campaign-unexclude-${c.id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2" data-testid="campaign-pace-box">
                <label className="text-xs text-foreground flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5 text-sky-400" /> Sending pace — slower pace &amp; daily limit help avoid spam filters
                </label>
                <PaceEditor pace={pace} onChange={setPace} />
              </div>
            </>
          )}

          <div>
            <label className="text-xs text-muted-foreground block mb-1">PDF attachment for the intro email (optional, max 3 MB)</label>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden"
              onChange={e => pickFile(e.target.files?.[0])} data-testid="campaign-file-input" />
            {file ? (
              <div className="flex items-center gap-2 text-xs text-foreground bg-secondary/40 border border-border rounded-sm px-2.5 py-1.5 w-fit" data-testid="campaign-file-chip">
                <Paperclip className="w-3.5 h-3.5 text-sky-400" /> {file.name} · {(file.size / 1024).toFixed(0)} KB
                <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-red-400" data-testid="campaign-file-remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="campaign-attach-btn"
                className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8">
                <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Attach PDF (search profile)
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {pastCampaigns.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="campaign-copy-box">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Copy className="w-3.5 h-3.5 text-sky-400" /> Reuse texts:
                </span>
                <Select value={copiedFrom} onValueChange={copyFrom}>
                  <SelectTrigger className="w-80 h-8 text-xs bg-secondary border-border rounded-sm" data-testid="campaign-copy-select">
                    <SelectValue placeholder="Copy subject & texts from a previous campaign…" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {pastCampaigns.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-xs" data-testid={`campaign-copy-option-${c.id}`}>
                        {c.name}{c.is_test ? ' · TEST' : ''} · {c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : ''} · {(c.steps || []).length} email{(c.steps || []).length !== 1 ? 's' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {steps.map((s, i) => (
              <div key={i} className="border border-border rounded-sm p-3 space-y-2 bg-secondary/20" data-testid={`campaign-step-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-sky-300">
                    {i === 0 ? 'Intro email' : `Follow-up ${i}`}
                  </span>
                  {i > 0 && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      after <Input type="number" min="1" max="90" value={s.delay_days}
                        onChange={e => setStep(i, 'delay_days', parseInt(e.target.value || '1', 10))}
                        data-testid={`campaign-step-delay-${i}`}
                        className="w-14 h-6 bg-secondary border-border text-foreground rounded-sm text-xs px-1.5 inline-block" /> {isTest ? 'minutes (test)' : 'days'}
                    </span>
                  )}
                  {i === 0 && file && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" />{file.name}</span>}
                  <span className="ml-auto flex items-center gap-2">
                    <button onClick={() => setPreview(p => ({ ...p, [i]: !p[i] }))}
                      data-testid={`campaign-step-preview-toggle-${i}`}
                      className={`flex items-center gap-1 text-[10px] rounded-sm border px-1.5 py-0.5 transition-colors ${preview[i] ? 'border-sky-500/50 bg-sky-500/10 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                      {preview[i] ? <><Pencil className="w-3 h-3" /> Edit</> : <><Eye className="w-3 h-3" /> Preview</>}
                    </button>
                    {i > 0 && (
                      <button onClick={() => removeStep(i)} className="text-muted-foreground hover:text-red-400" data-testid={`campaign-step-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                </div>
                {preview[i] ? (
                  <div data-testid={`campaign-step-preview-${i}`} className="w-0 min-w-full">
                    <p className="text-[10px] text-muted-foreground mb-1.5">
                      This is what the broker sees — rendered with {pvContact ? `real data of ${pvName}` : 'sample data (Erika Mustermann)'}.
                    </p>
                    <div className="bg-white text-zinc-900 rounded-sm border border-border p-4">
                      <div className="text-[11px] text-zinc-500 border-b border-zinc-200 pb-2 mb-3 space-y-0.5 break-words">
                        <p><span className="font-medium text-zinc-700">To:</span> {pvName} &lt;{pvCtx.email || '—'}&gt;</p>
                        <p><span className="font-medium text-zinc-700">Subject:</span> {renderTpl(s.subject, pvCtx) || <span className="italic">no subject</span>}</p>
                        {i === 0 && file && (
                          <p className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {file.name}</p>
                        )}
                      </div>
                      <div className="text-[13px] break-words [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_a]:text-blue-600 [&_a]:underline"
                        style={{ fontFamily: 'Calibri, Arial, Helvetica, sans-serif', lineHeight: 1.35 }}>
                        <div className="[&_p]:my-0"
                          dangerouslySetInnerHTML={{ __html: renderTpl(dedupLinks(asHtml(normalizeQuillHtml(s.template))), pvCtx) }} />
                        {!hasPastedSignature(s.template) && <SignaturePreview name={sigName} />}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Input value={s.subject} onChange={e => setStep(i, 'subject', e.target.value)} placeholder="Subject"
                      data-testid={`campaign-step-subject-${i}`} className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" />
                    <div data-testid={`campaign-step-body-${i}`}>
                      <ReactQuill theme="snow" value={s.template} onChange={(v) => setStep(i, 'template', v)}
                        modules={QUILL_MODULES} placeholder="Email text — bold, lists and links keep their formatting…" />
                    </div>
                    {hasPastedSignature(s.template) && (
                      <div className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2.5 py-2"
                        data-testid={`campaign-step-signature-hint-${i}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span className="flex-1">
                          The official OLTA signature (name, address, phone, website + confidentiality note) is appended
                          automatically — the signature in your text would appear twice.
                        </span>
                        <button onClick={() => { setStep(i, 'template', stripSignature(s.template)); toast.success('Pasted signature removed — the official one is appended on send'); }}
                          data-testid={`campaign-step-strip-signature-btn-${i}`}
                          className="shrink-0 flex items-center gap-1 text-[11px] font-medium border border-amber-500/60 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-sm px-2 py-1 transition-colors">
                          <Wand2 className="w-3 h-3" /> Remove pasted signature
                        </button>
                      </div>
                    )}
                    {needsCleanup(normalizeQuillHtml(s.template)) && (
                      <div className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2.5 py-2"
                        data-testid={`campaign-step-cleanup-${i}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span className="flex-1">
                          The pasted text contains hard line breaks or duplicated links ("[url]url") — the email would
                          arrive with broken lines. One click merges the wrapped lines and removes duplicates.
                        </span>
                        <button onClick={() => { setStep(i, 'template', cleanTemplate(s.template)); toast.success('Formatting fixed — check the preview before starting'); }}
                          data-testid={`campaign-step-cleanup-btn-${i}`}
                          className="shrink-0 flex items-center gap-1 text-[11px] font-medium border border-amber-500/60 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-sm px-2 py-1 transition-colors">
                          <Wand2 className="w-3 h-3" /> Fix formatting
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            {steps.length < 6 && (
              <Button variant="outline" size="sm" onClick={addStep} data-testid="campaign-add-step-btn"
                className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add follow-up
              </Button>
            )}
            <p className="text-[10px] text-muted-foreground">
              Placeholders: {'{first_name} {last_name} {title} {full_name} {company}'}{isTest && ' — rendered with sample data (Erika Mustermann) in test mode'}
              <span className="block mt-0.5">Your official OLTA signature ({sigName}, address, phone, website + confidentiality note) is appended automatically — end your text with the greeting line.</span>
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
              className="border-border text-muted-foreground rounded-sm text-xs h-9">Cancel</Button>
            <Button onClick={start} disabled={saving} data-testid="campaign-start-btn"
              className={`${isTest ? 'bg-violet-600 hover:bg-violet-500' : 'bg-sky-600 hover:bg-sky-500'} text-white rounded-sm text-xs h-9 px-4`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : isTest ? <FlaskConical className="w-4 h-4 mr-1.5" /> : <Megaphone className="w-4 h-4 mr-1.5" />}
              {isTest ? 'Start Test Campaign (1)' : `Start Campaign (${effectiveCount})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
